/**
 * Standardized loading spinner component using Lucide Loader2
 * Provides consistent loading states across the application
 */
import { Loader2 } from 'lucide-react';

export type SpinnerSize = 'sm' | 'md' | 'lg' | 'xl';
export type SpinnerVariant = 'page' | 'section' | 'inline' | 'button' | 'card';

interface LoadingSpinnerProps {
  /**
   * Size of the spinner
   * @default 'md'
   */
  size?: SpinnerSize;

  /**
   * Variant determines the layout and styling
   * - 'page': Full page centered spinner
   * - 'section': Centered within a container
   * - 'inline': Small inline spinner with optional text
   * - 'button': For use inside buttons
   * - 'card': For loading states in cards
   * @default 'section'
   */
  variant?: SpinnerVariant;

  /**
   * Optional loading message
   */
  message?: string;

  /**
   * Additional CSS classes
   */
  className?: string;

  /**
   * Color variant (not used with Lucide, kept for API compatibility)
   * @default 'blue'
   */
  color?: string;
}

const sizeMap: Record<SpinnerSize, number> = {
  sm: 16,
  md: 32,
  lg: 48,
  xl: 64,
};

export function LoadingSpinner({
  size = 'md',
  variant = 'section',
  message,
  className = '',
}: LoadingSpinnerProps) {
  const spinnerElement = (
    <Loader2
      size={sizeMap[size]}
      className={`animate-spin text-sky-600 dark:text-sky-400 ${className}`}
    />
  );

  switch (variant) {
    case 'page':
      return (
        <div className="min-h-screen flex flex-col items-center justify-center">
          {spinnerElement}
          {message && <p className="text-sm mt-4 text-zinc-600 dark:text-zinc-400">{message}</p>}
        </div>
      );

    case 'section':
      return (
        <div className="flex flex-col items-center justify-center py-12">
          {spinnerElement}
          {message && <p className="text-sm mt-4 text-zinc-600 dark:text-zinc-400">{message}</p>}
        </div>
      );

    case 'inline':
      return (
        <div className="inline-flex items-center gap-2">
          {spinnerElement}
          {message && <p className="text-sm text-zinc-600 dark:text-zinc-400">{message}</p>}
        </div>
      );

    case 'button':
      return (
        <div className="inline-flex items-center gap-2">
          {spinnerElement}
          {message && <span>{message}</span>}
        </div>
      );

    case 'card':
      return (
        <div className="flex flex-col items-center justify-center p-8">
          {spinnerElement}
          {message && <p className="text-sm mt-4 text-zinc-600 dark:text-zinc-400">{message}</p>}
        </div>
      );

    default:
      return spinnerElement;
  }
}

/**
 * Convenience components for common loading states
 */
export const PageLoadingSpinner = (props: Omit<LoadingSpinnerProps, 'variant'>) => (
  <LoadingSpinner {...props} variant="page" />
);

export const SectionLoadingSpinner = (props: Omit<LoadingSpinnerProps, 'variant'>) => (
  <LoadingSpinner {...props} variant="section" />
);

export const InlineLoadingSpinner = (props: Omit<LoadingSpinnerProps, 'variant'>) => (
  <LoadingSpinner {...props} variant="inline" />
);

export const ButtonLoadingSpinner = (props: Omit<LoadingSpinnerProps, 'variant'>) => (
  <LoadingSpinner {...props} variant="button" size="sm" />
);

export const CardLoadingSpinner = (props: Omit<LoadingSpinnerProps, 'variant'>) => (
  <LoadingSpinner {...props} variant="card" />
);
